import React from 'react';

import { BuiltInComponents } from 'craft/constants';
import { DocumentGrid } from 'craft/containers/Craft/DocumentGrid';
import { VIEWPORT_CONTAINER_ID, VIEWPORT_CONTENT_ID } from 'craft/containers/Viewport/constants';
import { useCraftViewportEventHandlers } from 'craft/containers/Viewport/modes/hooks/useCraftViewportEventHandlers';
import { useViewportDimensions } from 'craft/containers/Viewport/modes/hooks/useViewportDimensions';
import { useCurrentSnapshot, usePlacementMode, useViewportBreakpoint } from 'craft/state';
import { ActiveSnapshot } from 'dashboard/pages/Content/pages/Snapshots';

import { Content, ViewportContainer } from './components';
import { SnapshotLoader } from './components/SnapshotLoader';

export interface SnapshotViewportProps {
  children: React.ReactNode;
  contentId: string;
  rootNodeName: string;
  isRootSelected: boolean;
}

/**
 * Renders the editor with Snapshots in the background
 * Used by Modals, Tours and Hotspots
 *
 * Inline currently uses InlineSnapshotViewpot due to additional complexity of syncing viewport position and
 * dimensions
 *
 * Tours and Hotspots manage tooltip position from respective Resolver
 */
export const SnapshotViewport = React.memo(
  ({ children, contentId, rootNodeName, isRootSelected }: SnapshotViewportProps) => {
    const isModal = rootNodeName === BuiltInComponents.Modal;
    const viewportBreakpoint = useViewportBreakpoint();
    const { containerRef, contentRef } = useCraftViewportEventHandlers();
    const { placementMode } = usePlacementMode();

    const { isSnapshotLoaded } = useCurrentSnapshot();

    const { widthOverride, heightOverride } = useViewportDimensions({ rootNodeName });

    if (placementMode) {
      // if we're just in placement mode we only need the snapshot
      // but we should render with a consistent key to prevent re-fetching everything
      return (
        <ViewportContainer
          id={VIEWPORT_CONTAINER_ID}
          viewport={viewportBreakpoint}
          ref={(dom: any) => {
            containerRef.current = dom;
          }}
          data-cy="editor-viewport"
          height={heightOverride}
        >
          <ActiveSnapshot rootNodeName={rootNodeName} key={`snapshot-${contentId}`} />
        </ViewportContainer>
      );
    }

    return (
      <ViewportContainer
        id={VIEWPORT_CONTAINER_ID}
        viewport={viewportBreakpoint}
        ref={(dom: any) => {
          containerRef.current = dom;
        }}
        data-cy="editor-viewport"
        height={heightOverride}
      >
        <ActiveSnapshot rootNodeName={rootNodeName} key={`snapshot-${contentId}`} />
        {!isSnapshotLoaded ? (
          <SnapshotLoader />
        ) : (
          <Content
            id={VIEWPORT_CONTENT_ID}
            width={widthOverride}
            ref={(dom: any) => {
              contentRef.current = dom;
            }}
            withSnapshot
          >
            <DocumentGrid isSelected={!!isRootSelected} shouldHideLine={isModal} />
            {children}
          </Content>
        )}
      </ViewportContainer>
    );
  },
);
